import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Phone, Plus, ChevronDown, Check, Loader2, Download, Settings } from 'lucide-react';
import { usePhoneNumbers } from '@/hooks/usePhoneNumbers';
import { PhoneNumber } from '@/types/phoneNumber';
import { PhoneNumberPurchaser } from './PhoneNumberPurchaser';
import { PhoneNumberConfigModal } from './PhoneNumberConfigModal';
import { useToast } from '@/hooks/use-toast';

interface CampaignPhoneNumberSelectorProps {
  value?: string;
  onChange: (phoneNumber: string) => void;
  disabled?: boolean;
  selectedVapiKey: string; // Required for campaigns
  selectedVapiKeyName?: string; // The raw key name
}

// Helper function to normalize capabilities format
const normalizeCapabilities = (capabilities: any): string[] => {
  if (!capabilities) return [];
  if (Array.isArray(capabilities)) return capabilities;
  if (typeof capabilities === 'object') {
    return Object.entries(capabilities)
      .filter(([_, enabled]) => enabled)
      .map(([capability]) => capability);
  }
  return [];
};

export function CampaignPhoneNumberSelector({ 
  value, 
  onChange, 
  disabled,
  selectedVapiKey,
  selectedVapiKeyName
}: CampaignPhoneNumberSelectorProps) {
  const [showPurchaser, setShowPurchaser] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedForConfig, setSelectedForConfig] = useState<PhoneNumber | null>(null);
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [twilioNumbers, setTwilioNumbers] = useState<any[]>([]);
  const [selectedForImport, setSelectedForImport] = useState<Set<string>>(new Set());
  const [friendlyName, setFriendlyName] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const { toast } = useToast();
  const { 
    phoneNumbers, 
    loading, 
    error, 
    fetchPhoneNumbers, 
    formatPhoneNumber,
    fetchTwilioNumbers,
    importSelectedNumbers
  } = usePhoneNumbers();

  useEffect(() => {
    fetchPhoneNumbers();
  }, []);

  // Sort phone numbers by creation date (most recent first)
  const sortedNumbers = [...phoneNumbers].sort((a, b) => {
    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return dateB - dateA;
  });

  // Filter and limit phone numbers based on search state
  const displayNumbers = (() => {
    if (searchValue.trim()) {
      // When searching, show unlimited filtered results
      return sortedNumbers.filter(number => {
        if (!number.phoneNumber) return false;
        
        const search = searchValue.toLowerCase();
        const cleanedSearch = search.replace(/\D/g, '');
        const cleanedPhoneNumber = number.phoneNumber.replace(/\D/g, '');
        
        return (
          (number.friendlyName && number.friendlyName.toLowerCase().includes(search)) ||
          cleanedPhoneNumber.includes(cleanedSearch)
        );
      });
    } else {
      // When not searching, show only 10 most recent
      return sortedNumbers.slice(0, 10);
    }
  })();

  const selectedNumber = phoneNumbers.find(number => number.phoneNumber === value);

  const handleNumberSelect = (phoneNumber: string) => {
    onChange(phoneNumber);
    setOpen(false);
  };

  const handlePurchaseComplete = async (newNumber: PhoneNumber) => {
    setShowPurchaser(false);
    onChange(newNumber.phoneNumber);
    // Refresh phone numbers to ensure the new number shows up immediately
    await refreshPhoneNumbers();
  };

  const handleConfigureNumber = (phoneNumber: PhoneNumber) => {
    setSelectedForConfig(phoneNumber);
    setShowConfigModal(true);
  };

  const refreshPhoneNumbers = async () => {
    await fetchPhoneNumbers();
  };

  const handleImportClick = async () => {
    try {
      setImportLoading(true);
      const numbers = await fetchTwilioNumbers();
      setTwilioNumbers(numbers);
      setShowImport(true);
    } catch (error) {
      // Error already handled in hook
    } finally {
      setImportLoading(false);
    }
  };

  const handleNumberToggle = (phoneNumber: string) => {
    // Single selection for campaigns - only one number can be selected
    setSelectedForImport(new Set([phoneNumber]));
  };

  const handleImportSubmit = async () => {
    if (selectedForImport.size === 0) {
      toast({
        title: 'No Numbers Selected',
        description: 'Please select a number to import.',
        variant: 'destructive',
      });
      return;
    }

    if (!selectedVapiKeyName?.trim()) {
      toast({
        title: 'VAPI Account Required',
        description: 'No VAPI account selected in campaign settings.',
        variant: 'destructive',
      });
      return;
    }

    setImportLoading(true);
    try {
      const selectedNumbers = Array.from(selectedForImport).map(phoneNumber => 
        twilioNumbers.find(n => n.phoneNumber === phoneNumber)
      ).filter(Boolean);
      
      // Pass VAPI key name for server-side resolution
      const result = await importSelectedNumbers(selectedNumbers, selectedVapiKeyName);
      
      // Check if any numbers were actually imported
      if (result.imported > 0) {
        toast({
          title: 'Import Successful',
          description: `Successfully imported ${result.imported} number to ${selectedVapiKeyName}!`,
        });
        
        // Auto-select the first imported number
        if (result.results && result.results.length > 0) {
          onChange(result.results[0].phoneNumber);
        }
        
        setShowImport(false);
        setSelectedForImport(new Set());
        setFriendlyName('');
      } else {
        // Import failed - show errors and keep dialog open
        const errorMessages = result.errors || ['No numbers were imported'];
        toast({
          title: 'Import Failed',
          description: errorMessages.join(', '),
          variant: 'destructive',
        });
        // Don't close the dialog so user can retry
      }
    } catch (error) {
      console.error('Campaign phone import failed:', error);
      toast({
        title: 'Import Failed',
        description: error instanceof Error ? error.message : 'Failed to import numbers',
        variant: 'destructive',
      });
      // Don't close the dialog so user can retry
    } finally {
      setImportLoading(false);
    }
  };

  if (loading && phoneNumbers.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        <span>Loading phone numbers...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="pt-6">
          <p className="text-destructive text-sm">{error}</p>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => fetchPhoneNumbers()}
            className="mt-2"
          >
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">
          AI Calling Phone Number
        </Label>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleImportClick}
            disabled={disabled || importLoading}
            className="text-xs"
          >
            {importLoading ? (
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            ) : (
              <Download className="w-3 h-3 mr-1" />
            )}
            Import from Twilio
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowPurchaser(true)}
            disabled={disabled}
            className="text-xs"
          >
            <Plus className="w-3 h-3 mr-1" />
            Get New Number
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={refreshPhoneNumbers}
            disabled={disabled || loading}
            className="text-xs"
          >
            <span className="text-lg">🔄</span>
            Refresh
          </Button>
        </div>
      </div>

      {phoneNumbers.length > 0 ? (
        <div className="space-y-3">
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className="w-full justify-between h-auto p-3"
                disabled={disabled}
              >
                {selectedNumber ? (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    <div className="flex flex-col text-left">
                      {selectedNumber.friendlyName && (
                        <span className="font-medium">{selectedNumber.friendlyName}</span>
                      )}
                      <span className={selectedNumber.friendlyName ? "text-muted-foreground text-sm" : ""}>
                        {selectedNumber.phoneNumber ? formatPhoneNumber(selectedNumber.phoneNumber) : 'No number'}
                      </span>
                    </div>
                  </div>
                ) : (
                  "Select AI calling phone number..."
                )}
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0" align="start">
              <Command>
                <CommandInput 
                  placeholder="Search phone numbers..." 
                  className="h-9"
                  value={searchValue}
                  onValueChange={setSearchValue}
                />
                <CommandEmpty>
                  {searchValue.trim() ? 'No phone numbers match your search.' : 'No phone numbers found.'}
                </CommandEmpty>
                <CommandList>
                  <CommandGroup>
                    {displayNumbers.map((number) => (
                      <CommandItem
                        key={number.id}
                        value={`${number.friendlyName || ''} ${number.phoneNumber}`.toLowerCase()}
                        onSelect={() => handleNumberSelect(number.phoneNumber)}
                        className="flex items-center justify-between py-3"
                      >
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          <div>
                            {number.friendlyName && (
                              <div className="font-medium">
                                {number.friendlyName}
                              </div>
                            )}
                            <div className={number.friendlyName ? "text-xs text-muted-foreground" : "font-medium"}>
                              {number.phoneNumber ? formatPhoneNumber(number.phoneNumber) : 'No number'}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex gap-1">
                            {normalizeCapabilities(number.capabilities).map((capability) => (
                              <Badge key={capability} variant="secondary" className="text-xs">
                                {capability.toUpperCase()}
                              </Badge>
                            ))}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleConfigureNumber(number);
                            }}
                            className="h-6 w-6 p-0"
                            title="Configure phone number"
                          >
                            <Settings className="h-3 w-3" />
                          </Button>
                          <Check
                            className={`ml-auto h-4 w-4 ${
                              value === number.phoneNumber ? "opacity-100" : "opacity-0"
                            }`}
                          />
                        </div>
                      </CommandItem>
                    ))}
                    {!searchValue.trim() && phoneNumbers.length > 10 && (
                      <div className="px-2 py-1 text-xs text-muted-foreground text-center border-t">
                        Showing 10 most recent numbers. Search to see all {phoneNumbers.length} numbers.
                      </div>
                    )}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {selectedNumber && (
            <Card className="bg-muted/50">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">
                      {selectedNumber.phoneNumber ? formatPhoneNumber(selectedNumber.phoneNumber) : 'No number'}
                    </p>
                    {selectedNumber.friendlyName && (
                      <p className="text-sm text-muted-foreground">
                        {selectedNumber.friendlyName}
                      </p>
                    )}
                    {selectedNumber.locality && selectedNumber.region && (
                      <p className="text-xs text-muted-foreground">
                        {selectedNumber.locality}, {selectedNumber.region}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <Badge variant="outline" className="text-xs">
                      {selectedNumber.provider.toUpperCase()}
                    </Badge>
                    <div className="flex gap-1">
                      {normalizeCapabilities(selectedNumber.capabilities).map((capability) => (
                        <Badge key={capability} variant="secondary" className="text-xs">
                          {capability.toUpperCase()}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      ) : (
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base">No AI Calling Numbers Found</CardTitle>
            <CardDescription>
              You don't have any phone numbers set up for AI calling. Import existing Twilio numbers or purchase a new one to get started.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button
              type="button"
              onClick={handleImportClick}
              disabled={disabled || importLoading}
              className="w-full"
              variant="default"
            >
              {importLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Import from Twilio
            </Button>
            <Button
              type="button"
              onClick={() => setShowPurchaser(true)}
              disabled={disabled}
              className="w-full"
              variant="outline"
            >
              <Plus className="w-4 h-4 mr-2" />
              Get New Number
            </Button>
          </CardContent>
        </Card>
      )}

      <PhoneNumberPurchaser
        isOpen={showPurchaser}
        onClose={() => setShowPurchaser(false)}
        onPurchaseComplete={handlePurchaseComplete}
        provider="twilio"
        onStateRefresh={refreshPhoneNumbers}
        selectedVapiKey={selectedVapiKey}
        selectedVapiKeyName={selectedVapiKeyName}
      />

      <PhoneNumberConfigModal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        phoneNumber={selectedForConfig}
        onUpdate={refreshPhoneNumbers}
      />

      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import Numbers for AI Calling</DialogTitle>
            <DialogDescription>
              Select existing Twilio phone numbers to import and set up for AI calling with VAPI integration.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Show which VAPI key will be used */}
            <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
              <Settings className="w-4 h-4 text-muted-foreground" />
              <div className="text-sm text-muted-foreground">
                Using VAPI Account: {selectedVapiKeyName || 'Default Account'}
              </div>
            </div>

            {/* Friendly Name Input */}
            <div className="space-y-2">
              <Label htmlFor="friendlyName">Friendly Name (optional)</Label>
              <Input
                id="friendlyName"
                placeholder="e.g., AI Sales Assistant"
                value={friendlyName}
                onChange={(e) => setFriendlyName(e.target.value)}
              />
            </div>

            {twilioNumbers.length > 0 ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">
                    Available Numbers ({twilioNumbers.length})
                  </Label>
                  <div className="text-xs text-muted-foreground">
                    {selectedForImport.size} selected
                  </div>
                </div>
                
                <div className="border rounded-lg max-h-60 overflow-y-auto">
                  {twilioNumbers.map((number) => (
                    <div
                      key={number.phoneNumber}
                      className="flex items-center justify-between p-3 border-b last:border-b-0 hover:bg-muted/50"
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="selectedNumber"
                          checked={selectedForImport.has(number.phoneNumber)}
                          onChange={() => handleNumberToggle(number.phoneNumber)}
                          className="w-4 h-4 text-primary bg-background border-2 border-border focus:ring-2 focus:ring-primary"
                        />
                        <div>
                          <div className="font-medium">
                            {formatPhoneNumber(number.phoneNumber)}
                          </div>
                          {number.friendlyName && (
                            <div className="text-sm text-muted-foreground">
                              {number.friendlyName}
                            </div>
                          )}
                          <div className="flex gap-1 mt-1">
                            {normalizeCapabilities(number.capabilities).map((capability: string) => (
                              <Badge key={capability} variant="secondary" className="text-xs">
                                {capability.toUpperCase()}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No Twilio numbers found</p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setShowImport(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleImportSubmit}
                disabled={selectedForImport.size === 0 || importLoading}
              >
                {importLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  `Import Selected Number`
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}